import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, ShoppingBag, Search, Coffee } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import ProductDetailModal from '../components/ProductDetailModal';
import ChefPickBadge from '../components/ChefPickBadge';
import { localizedField as i18nLocalized } from '../utils/i18nFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Beverages = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const { addItem } = useCart();
  const { language } = useLanguage();

  const texts = {
    tr: { title: 'Icecek Menusu', subtitle: 'Sicak ve soguk icecek cesitlerimiz', searchPlaceholder: 'Icecek ara...', all: 'Tumsu', added: 'Eklendi', noResults: 'Aradiginiz kriterlere uygun icecek bulunamadi.' },
    en: { title: 'Beverages', subtitle: 'Our hot and cold beverage selection', searchPlaceholder: 'Search beverages...', all: 'All', added: 'Added', noResults: 'No beverages found matching your criteria.' },
    me: { title: 'Pica', subtitle: 'Nasi topli i hladni napici', searchPlaceholder: 'Pretrazi pica...', all: 'Sve', added: 'Dodato', noResults: 'Nema pica koja odgovaraju vasim kriterijumima.' },
    ru: { title: 'Напитки', subtitle: 'Наш выбор горячих и холодных напитков', searchPlaceholder: 'Поиск напитков...', all: 'Все', added: 'Добавлено', noResults: 'Напитки по вашим критериям не найдены.' },
    sr: { title: 'Пића', subtitle: 'Наши топли и хладни напици', searchPlaceholder: 'Претражи пића...', all: 'Све', added: 'Додато', noResults: 'Нема пића по вашим критеријумима.' }
  };
  const txt = texts[language] || texts.en;

  const getItemName = (item) => i18nLocalized(item, 'name', language);
  const getItemDescription = (item) => i18nLocalized(item, 'description', language);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [itemsRes, catRes] = await Promise.all([
          axios.get(`${API}/beverages`),
          axios.get(`${API}/beverages/categories`)
        ]);
        setItems(itemsRes.data);
        setCategories(catRes.data);
      } catch {
        toast.error('Icecek menusu yuklenirken hata olustu');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = items.filter(item => {
    const matchesCat = selectedCategory === 'all' || item.category === selectedCategory;
    const name = getItemName(item).toLowerCase();
    const desc = getItemDescription(item).toLowerCase();
    const matchesSearch = name.includes(searchQuery.toLowerCase()) || desc.includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const handleModalAdd = (item, qty) => {
    addItem({ item_id: item.id, name: getItemName(item), name_tr: item.name, price: item.price, quantity: qty, item_type: 'beverage' });
    toast.success(`${qty}x ${getItemName(item)} ${txt.added}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-terracotta border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <div className="bg-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-4">{txt.title}</h1>
            <p className="text-charcoal/60 text-lg">{txt.subtitle}</p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-charcoal/40" />
            <Input
              type="text"
              placeholder={txt.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-white border-stone-200 rounded-xl h-12"
              data-testid="beverage-search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              className={`rounded-full whitespace-nowrap ${selectedCategory === 'all' ? 'bg-basil text-white hover:bg-basil/90' : 'bg-white hover:bg-stone-50'}`}
              data-testid="bev-category-all-btn"
            >
              {txt.all}
            </Button>
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat)}
                className={`rounded-full whitespace-nowrap ${selectedCategory === cat ? 'bg-basil text-white hover:bg-basil/90' : 'bg-white hover:bg-stone-50'}`}
                data-testid={`bev-category-${cat}`}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((item, index) => (
              <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ delay: index * 0.05 }} layout>
                <BeverageCard item={item} getItemName={getItemName} getItemDescription={getItemDescription} onDetail={(i) => setSelectedItem({ ...i, _displayName: getItemName(i), _displayDesc: getItemDescription(i), _displayCategory: i.category })} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16">
            <Coffee className="w-16 h-16 text-charcoal/20 mx-auto mb-4" />
            <p className="text-charcoal/60 text-lg">{txt.noResults}</p>
          </div>
        )}
      </div>

      <ProductDetailModal
        item={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onAddToCart={handleModalAdd}
        language={language}
      />
    </div>
  );
};

const BeverageCard = ({ item, getItemName, getItemDescription, onDetail }) => {
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useCart();
  const { language } = useLanguage();
  const itemName = getItemName(item);

  const btnTexts = { tr: { add: 'Ekle', added: 'sepete eklendi' }, en: { add: 'Add', added: 'added to cart' }, me: { add: 'Dodaj', added: 'dodato u korpu' } };
  const bt = btnTexts[language] || btnTexts.en;

  const handleAdd = () => {
    addItem({ item_id: item.id, name: itemName, name_tr: item.name, price: item.price, quantity, item_type: 'beverage' });
    toast.success(`${quantity}x ${itemName} ${bt.added}`);
    setQuantity(1);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-stone-100/50 cursor-pointer" onClick={() => onDetail?.(item)} data-testid={`beverage-card-${item.id}`}>
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-100">
        {item.image_url ? (
          <img
            src={item.image_url.startsWith('/api') ? `${API.replace('/api', '')}${item.image_url}` : item.image_url}
            alt={itemName}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Coffee className="w-12 h-12 text-stone-300" />
        </div>
        {item.is_featured && (
          <div className="absolute top-3 left-3"><ChefPickBadge language={language} /></div>
        )}
      </div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-playfair text-xl font-semibold text-charcoal">{itemName}</h3>
          <span className="text-terracotta font-bold text-lg">€{item.price}</span>
        </div>
        <p className="text-charcoal/60 text-sm mb-4 line-clamp-2">{getItemDescription(item)}</p>
        <span className="inline-block px-3 py-1 bg-sand rounded-full text-xs font-medium text-charcoal/70 mb-4">{item.category}</span>
        {item.is_available && (
          <div className="flex items-center gap-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50" data-testid={`bev-qty-minus-${item.id}`}>
                <Minus className="w-4 h-4 text-charcoal" />
              </button>
              <span className="w-8 text-center font-medium text-charcoal">{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50" data-testid={`bev-qty-plus-${item.id}`}>
                <Plus className="w-4 h-4 text-charcoal" />
              </button>
            </div>
            <Button onClick={handleAdd} className="flex-1 bg-terracotta text-white hover:bg-terracotta/90 rounded-full" data-testid={`bev-add-${item.id}`}>
              <ShoppingBag className="w-4 h-4 mr-2" />{bt.add}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Beverages;
